import { Card, IconButton, LuIcon, Text } from "@overmap-ai/blocks"
import { OrganizationAccessLevel, UNLABELLED_ASSET_LABEL } from "@overmap-ai/common"
import { selectAssetById } from "@overmap-ai/core"
import { cx } from "class-variance-authority"
import { type ComponentProps, type ComponentPropsWithRef, memo, type MouseEventHandler, useCallback } from "react"

import { interactiveCardCva } from "../../cva"
import { modelIconMapping } from "../../mappings/icons"
import { useAppSelector } from "../../store"
import { AssetTypeIcon } from "../Icons/AssetTypeIcon"
import { AssetIndexLabel } from "../IndexLabels/AssetIndexLabel"
import { AssetLink } from "../Links"
import { OrganizationAccessRequired } from "../OrganizationAccessRequired/AccessRequired"

interface IssueAssociationAssetListItemProps extends ComponentPropsWithRef<"div"> {
	assetId: string
	onDelete: () => void
	size?: ComponentProps<typeof Card>["size"]
}

export const IssueAssociationAssetListItem = memo<IssueAssociationAssetListItemProps>((props) => {
	const { className, assetId, onDelete, size = "sm", ...rest } = props

	const asset = useAppSelector(selectAssetById(assetId))

	if (!asset) {
		throw new Error(`Asset with id ${assetId} not found in store.`)
	}

	const handleDelete = useCallback<MouseEventHandler<HTMLButtonElement>>(
		(e) => {
			e.stopPropagation()
			onDelete?.()
		},
		[onDelete],
	)

	return (
		<Card
			className={cx(className, "flex cursor-pointer justify-between gap-2", interactiveCardCva())}
			role="listitem"
			size={size}
			{...rest}
		>
			<AssetLink className="flex min-w-0 grow items-center gap-2" assetId={asset.uuid}>
				<AssetTypeIcon assetTypeId={asset.asset_type} />
				<AssetIndexLabel assetId={asset.uuid} size={size} />
				<Text accentColor="base" size={size}>
					{asset.label || UNLABELLED_ASSET_LABEL}
				</Text>
			</AssetLink>
			<div className="flex items-center justify-center pl-0">
				<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.ADMIN} type="hide">
					<IconButton
						aria-label="delete"
						accentColor="base"
						size={size}
						variant="ghost"
						onClick={handleDelete}
					>
						<LuIcon icon={modelIconMapping.close} />
					</IconButton>
				</OrganizationAccessRequired>
			</div>
		</Card>
	)
})
IssueAssociationAssetListItem.displayName = "IssueAssociationAssetListItem"
